import { useEffect } from 'react';

const SITE_NAME = "Al's-Ops LLC";
const DEFAULT_SITE = 'https://alsopss.com';
const DEFAULT_OG_IMAGE = 'https://alsopss.com/og-image.png?v=3';

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => {
    el.setAttribute(key, value);
  });
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

function upsertJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-seo-id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return el;
}

function Seo({ title, description, canonical, keywords, ogImage, ogImageAlt, jsonLd, noindex = false }) {
  useEffect(() => {
    if (title) {
      document.title = title;
      upsertMeta('meta[name="title"]', { name: 'title', content: title });
      upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
      upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    }

    if (description) {
      upsertMeta('meta[name="description"]', { name: 'description', content: description });
      upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
      upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    }

    if (keywords && keywords.length > 0) {
      upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywords.join(', ') });
    }

    const resolvedCanonical = canonical || DEFAULT_SITE;
    if (noindex) document.head.querySelector('link[rel="canonical"]')?.remove();
    else upsertLink('canonical', resolvedCanonical);
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: resolvedCanonical });
    upsertMeta('meta[name="twitter:url"]', { name: 'twitter:url', content: resolvedCanonical });

    const resolvedOgImage = ogImage || DEFAULT_OG_IMAGE;
    const resolvedOgImageAlt = ogImageAlt || `${SITE_NAME} — Vigilhold server monitoring platform`;
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: resolvedOgImage });
    upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: resolvedOgImageAlt });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: resolvedOgImage });
    upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: resolvedOgImageAlt });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow' });
    upsertMeta('meta[name="googlebot"]', { name: 'googlebot', content: noindex ? 'noindex, nofollow' : 'index, follow' });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_US' });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });

    let injectedEl = null;
    if (jsonLd) {
      injectedEl = upsertJsonLd('page-schema', jsonLd);
    }
    return () => {
      if (injectedEl) injectedEl.remove();
    };
  }, [title, description, canonical, keywords, ogImage, ogImageAlt, jsonLd, noindex]);

  return null;
}

export default Seo;
